/**
 * StatusDot — a 6px semantic dot, optionally with the word beside it.
 *
 * Colour alone never carries the meaning: pass `label` (or rely on the built-in
 * `aria-label`) so the state survives greyscale and screen readers.
 */

import type { HTMLAttributes, ReactNode } from 'react';
import type { Status } from '@shared/types';
import { cn } from '@/lib/cn';
import { STATUS_META } from '@/lib/constants';
import { mixSurface } from './Badge';

export type DotTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'accent' | 'muted';

const TONE: Record<DotTone, string> = {
  neutral: 'var(--text-secondary)',
  info: 'var(--info)',
  success: 'var(--success)',
  warning: 'var(--warning)',
  danger: 'var(--danger)',
  accent: 'var(--accent)',
  muted: 'var(--text-muted)',
};

/*
  A domain status paints `STATUS_META[status].accent` — the same value the inbox
  badge, the filter chip and the activity feed resolve — and only a tone-ONLY
  caller (presence, connection health) falls back to the `TONE` table above.

  Both halves used to be declared here again. The private tone map disagreed
  with the inbox cell's about `archived` (`muted` here, `neutral` there: two
  different greys for one status), and routing a domain status through `TONE` at
  all meant "Lost" painted `--text-secondary` #b4c7dc — the body-ink grey —
  8px from a Stage dot painting `--stage-lost` #7e93ab for the same word on the
  same record. Two greys, one meaning, and the one that carried the meaning was
  the one the status control did not use.
*/

export interface StatusDotProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** Domain status — sets both the tone and the default label. */
  status?: Status;
  /** Overrides the tone when there is no domain status (presence, health, …). */
  tone?: DotTone;
  /** Text beside the dot. Omit for a bare dot; the accessible name is kept. */
  label?: ReactNode;
  /** Breathing halo, for "live" states only. */
  pulse?: boolean;
  size?: 'sm' | 'md';
}

export function StatusDot({
  status,
  tone,
  label,
  pulse = false,
  size = 'md',
  className,
  ...rest
}: StatusDotProps) {
  const resolvedTone = tone ?? (status ? STATUS_META[status].tone : 'neutral');
  /* The status table wins whenever there is a status; `TONE` serves the
     tone-only callers. */
  const color = status !== undefined && tone === undefined ? STATUS_META[status].accent : TONE[resolvedTone];
  const accessibleName =
    typeof label === 'string' ? label : status ? STATUS_META[status].label : resolvedTone;

  const dot = (
    <span
      className={cn('relative inline-flex shrink-0', size === 'sm' ? 'size-1.5' : 'size-2')}
    >
      {pulse ? (
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-full"
          style={{
            background: mixSurface(color, 55),
            animation:
              'af-fade-out calc(var(--dur-slow)*3) var(--ease-in-out) infinite alternate',
            transform: 'scale(1.9)',
          }}
        />
      ) : null}
      <span
        aria-hidden="true"
        className="relative size-full rounded-full"
        style={{ background: color }}
      />
    </span>
  );

  if (label === undefined) {
    return (
      <span
        role="img"
        aria-label={accessibleName}
        className={cn('inline-flex items-center', className)}
        {...rest}
      >
        {dot}
      </span>
    );
  }

  return (
    <span className={cn('inline-flex items-center gap-1.5 whitespace-nowrap', className)} {...rest}>
      {dot}
      {/* 13px on the app's 20px body line. `text-base` alone resolves to
          Tailwind's 1.5 ratio — 19.5px — which is the one line box in the
          record pane that did not land on a whole pixel. */}
      <span className="text-base leading-[20px] text-secondary">{label}</span>
    </span>
  );
}
